import { Injectable, inject } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { last } from 'rxjs/operators';

import { BulkActionsController, BulkActionType } from '@core/bulk-actions-legacy';
import type {
  ActionsVisibilityBreakpoint,
  BulkAction,
  GetBulkActionsProps,
} from '@core/bulk-actions-legacy';
import { ReferralsService } from '@core/referrals-legacy';
import type { Requisition } from '@core/requisitions-types';
import { RequisitionsScope } from '@core/requisitions-types';

import { RequisitionsBulkActionsExecutorService } from './requisitions-bulk-actions-executor.service';

@Injectable()
export class RequisitionsBulkActionsController extends BulkActionsController {
  private readonly executor = inject(RequisitionsBulkActionsExecutorService);

  protected readonly actions$ = inject(Actions);
  protected readonly referralsService = inject(ReferralsService);

  protected availableActions: BulkAction[] = [
    {
      id: BulkActionType.Follow,
      buttonLabel: this.i18n.translate('app.bulk_actions.follow'),
      buttonIcon: 'bookmark',
      analytics: '[Requisitions] Toolbar_BulkActionsFollowButtonClick',
    },
    {
      id: BulkActionType.Unfollow,
      buttonLabel: this.i18n.translate('app.bulk_actions.unfollow'),
      buttonIcon: 'bookmark-border',
      analytics: '[Requisitions] Toolbar_BulkActionsUnfollowButtonClick',
    },
    {
      id: BulkActionType.Archive,
      buttonLabel: this.i18n.translate('app.bulk_actions.archive'),
      buttonIcon: 'archive',
      analytics: '[Requisitions] Toolbar_BulkActionsArchiveButtonClick',
    },
    {
      id: BulkActionType.RetrieveArchived,
      buttonLabel: this.i18n.translate('app.bulk_actions.retrieve_archived_jobs'),
      buttonIcon: 'unarchive',
      analytics: '[Requisitions] Toolbar_BulkActionsRetrieveButtonClick',
    },
  ];

  public getBulkActions(props: GetBulkActionsProps): BulkAction[] {
    return props?.requisitionsScope !== RequisitionsScope.Archived
      ? this.getActionsForActiveRequisitions()
      : this.getActionsForArchivedRequisitions();
  }

  public dispatchAction(data: { action: BulkAction }): void {
    const { action } = data;
    const requisitions = this.selectedItems$.getValue();

    if (action.analytics) {
      typeof action.analytics === 'string'
        ? this.analytics.track(action.analytics)
        : action.analytics();
    }

    const bulkActionExecution$ = this.executor.getExecutionStream(
      action,
      <Requisition[]>requisitions,
    );
    bulkActionExecution$.pipe(last()).subscribe({
      next: confirmed => {
        if (confirmed) {
          this.markBulkActionAsCompleted(action);
          this.clearSelection();
        }
      },
      error: error => {
        this.markBulkActionAsCompleted(action);
        this.dispatchActionError(action, error);
      },
    });
  }

  public getActionsVisibilityByBreakpoint(): ActionsVisibilityBreakpoint[] {
    return [
      {
        min: 0,
        max: Number.POSITIVE_INFINITY,
        actionIds: [BulkActionType.Follow, BulkActionType.Unfollow],
      },
    ];
  }

  private getActionsForActiveRequisitions(): BulkAction[] {
    return this.availableActions.filter(action => {
      switch (action.id) {
        case BulkActionType.Follow:
        case BulkActionType.Unfollow:
        case BulkActionType.Archive:
          return true;

        default:
          return false;
      }
    });
  }

  private getActionsForArchivedRequisitions(): BulkAction[] {
    return this.availableActions.filter(action => {
      switch (action.id) {
        case BulkActionType.Follow:
        case BulkActionType.Unfollow:
        case BulkActionType.RetrieveArchived:
          return true;
        default:
          return false;
      }
    });
  }
}
